import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const CodeBlock = makeShortcode("CodeBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "search-filters"
    }}>{`Search filters`}</h1>
    <p>{`Use search filters to limit the data returned so that reports show only the values you need.  For example, if you have thousands of records but only a few have needed reports, you can use filtering to return and find them quickly. Some filters also allow you to include, group, or present data in convenient formats, such as breakdown reports.`}</p>
    <p>{`You can use the `}<inlineCode parentName="p">{`search`}</inlineCode>{` parameter to filter your results more narrowly. The `}<inlineCode parentName="p">{`search`}</inlineCode>{` parameter includes the following options:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`itemId`}</inlineCode>{` - A single ID to include in the report`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`itemIds`}</inlineCode>{` - A list of itemIds to include in the report (shown in the example)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`excludeItemIds`}</inlineCode>{` - A list of itemIds to exclude in the report`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`clause`}</inlineCode>{` - A search clause to use when filtering dimensions`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`includeSearchTotal`}</inlineCode>{` - Includes a special element called 'searchTotals' in the response that contains the total of the filtered items. The default is `}<inlineCode parentName="li">{`false`}</inlineCode>{`.`}</li>
    </ul>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Adobe may add optional request and response members (name/value pairs) to existing API objects at any time and without notice or changes in versioning. Adobe recommends that you refer to the API documentation of any third-party tool you integrate with our APIs so that such additions are ignored in processing if not understood. If implemented properly, such additions are non-breaking changes for your implementation. Adobe will not remove parameters or add required parameters without first providing standard notification through release notes.`}</p>
    <h2 {...{
      "id": "example-search-request"
    }}>{`Example `}<inlineCode parentName="h2">{`search`}</inlineCode>{` Request`}</h2>
    <p>{`The following example requests data only for campaigns "10" and "11" with the `}<inlineCode parentName="p">{`search`}</inlineCode>{` parameter by using their itemIds (line 9). The response returns the data requested for the `}<inlineCode parentName="p">{`itemId`}</inlineCode>{` associated with campaigns 10 and 11 (lines 18-21 and 25-28).`}</p>
    <CodeBlock slots="heading, code" repeat="2" languages="JSON,JSON" mdxType="CodeBlock" />
    <h4 {...{
      "id": "request-body"
    }}>{`Request body`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
   "rsid":"examplersid",
   "globalFilters":[
      {
         "type":"dateRange",
         "dateRange":"YYYY-01-31T00:00:00.000/YYYY-02-06T23:59:59.999"
      }
   ],
   "search":{
      "itemIds":[743855946,511036305]
   },
   "metricContainer":{
      "metrics":[
         {
            "columnId":"0",
            "id":"metrics/pageviews",
            "filters":[
               "0"
            ]
         }
      ],
      "metricFilters":[
         {
            "id":"0",
            "type":"dateRange",
            "dateRange":"YYYY-01-31T00:00:00.000/YYYY-02-06T23:59:59.999"
         }
      ]
   },
   "dimension":"variables/evar1",
   "settings":{
      "dimensionSort":"asc",
      "limit":5
   }
}
`}</code></pre>
    <h4 {...{
      "id": "response"
    }}>{`Response`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
   "totalPages":1,
   "firstPage":true,
   "lastPage":true,
   "numberOfElements":2,
   "number":0,
   "totalElements":2,
   "columns":{
      "dimension":{
         "id":"variables/evar1",
         "type":"string"
      },
      "columnIds":[
         "0"
      ]
   },
   "rows":[
      {
         "itemId":"511036305",
         "value":"11",
         "data":[
            1631.0
         ]
      },
      {
         "itemId":"743855946",
         "value":"10",
         "data":[
            2032.0
         ]
      }
   ],
   "summaryData":{
      "totals":[
         104310.0
      ]
   }
}
`}</code></pre>
    <h2 {...{
      "id": "using-clause-parameters"
    }}>{`Using `}<inlineCode parentName="h2">{`clause`}</inlineCode>{` Parameters`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`search`}</inlineCode>{` parameter also includes the `}<inlineCode parentName="p">{`clause`}</inlineCode>{` option. The `}<inlineCode parentName="p">{`clause`}</inlineCode>{` parameter provides a powerful tool for filtering data. To use it, follow these rules:`}</p>
    <ul>
      <li parentName="ul">{`It uses boolean operators `}<inlineCode parentName="li">{`AND`}</inlineCode>{`, `}<inlineCode parentName="li">{`OR`}</inlineCode>{`, and `}<inlineCode parentName="li">{`NOT`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`It uses operators `}<inlineCode parentName="li">{`MATCH`}</inlineCode>{`, `}<inlineCode parentName="li">{`CONTAINS`}</inlineCode>{`, `}<inlineCode parentName="li">{`BEGINS-WITH`}</inlineCode>{`, and `}<inlineCode parentName="li">{`ENDS-WITH`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`It uses group conditions with parenthesis.`}</li>
      <li parentName="ul">{`Strings are contained in single quotes.`}</li>
      <li parentName="ul">{`Searches are case-insensitive.`}</li>
      <li parentName="ul">{`If no operator is specified, a 'contains' match is performed.`}</li>
      <li parentName="ul">{`Valid operators are 'match' and 'contains'.`}</li>
      <li parentName="ul">{`Glob expressions are evaluated. If a literal `}<inlineCode parentName="li">{`*`}</inlineCode>{` is needed, use `}<inlineCode parentName="li">{`\\*`}</inlineCode>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "example-clause-statements"
    }}>{`Example Clause statements`}</h3>
    <ul>
      <li parentName="ul">{`Only include results that match the string 'home page': `}<inlineCode parentName="li">{`MATCH 'home page'`}</inlineCode></li>
      <li parentName="ul">{`Include pages that do not contain 'home page': `}<inlineCode parentName="li">{`NOT CONTAINS 'home page'`}</inlineCode></li>
      <li parentName="ul">{`Include pages that do not contain 'home page' or 'about us', but do contain 'contact us': `}<inlineCode parentName="li">{`(NOT CONTAINS 'home page' OR NOT CONTAINS 'about us') AND (CONTAINS 'contact us')`}</inlineCode></li>
      <li parentName="ul">{`Include pages that contain 'home page' or start with 'landing': `}<inlineCode parentName="li">{`CONTAINS 'home page' OR BEGINS-WITH 'landing'`}</inlineCode></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      